<script>
import { mapGetters, mapActions } from "vuex";
import Header from "@/components/Header.vue";
import Snackbar from "@/components/Snackbar.vue";
import AutoLogout from "@/components/AutoLogout.vue";
import LoadingOverlay from "@/components/LoadingOverlay.vue";
import Susie from "@/components/Susie.vue";
import { CONFIG } from "@/config";
import { localizationMixin } from "./mixins/localizationMixin";
import { eventBus } from "./main";
import { loadLanguageAsync } from "./i18n";

export default {
  name: "App",
  mixins: [localizationMixin],

  components: {
    Header,
    Snackbar,
    AutoLogout,
    LoadingOverlay,
    Susie,
  },

  data: () => ({ accessCode: "" }),

  watch: {
    "$i18n.locale"() {
      const meta = this.$route.meta;
      if (meta?.titleI18nKey) {
        document.title = this.$t(meta.titleI18nKey).trim();
      }
    },
  },

  computed: {
    ...mapGetters(["isLoading", "getClientConfig", "selectedLanguage", "isAuthenticated"]),

    isSurveyModuleEnabled() {
      return this.getClientConfig?.surveyModuleEnabled === true;
    },

    appclass() {
      var classname = "";
      if (window.frameElement && window.frameElement.nodeName.toLowerCase() == "iframe") {
        classname = "hidebranding ";
      }
      if (this.$route.path.toLowerCase() == "/") {
        classname += "loginpage";
      } else if (this.$route.path.toLowerCase() == "/home") {
        classname += "homepage";
      } else if (this.$route.path.toLowerCase().includes("/accesscode")) {
        classname += "acpage";
      } else if (this.$route.path.toLowerCase() == "/accesscoderesult") {
        classname += "acrpage";
      }
      return classname;
    },

    termsofUse() {
      return (
        CONFIG.PORTAL_BASE_URL +
        this.getClientConfig.clientname +
        "/" +
        this.selectedLanguage +
        "/Family+Portal+Terms+of+Use.pdf"
      );
    },

    termsofUseTexas() {
      if (this.selectedLanguage == "es") {
        return CONFIG.TERMS_OF_USE_TEXAS + "-es.html";
      } else {
        return CONFIG.TERMS_OF_USE_TEXAS + ".html";
      }
    },

    cancellation() {
      return CONFIG.TEXAS_CANCELLATION_TEST;
    },
  },

  methods: {
    ...mapActions([
      "UPDATE_ACCESSIBILITY_CLASS_LIST",
      "UPDATE_ACCESSIBILITY_IS_ACTIVE",
      "SET_SELECTED_LANGUAGE",
      "SET_LANGUAGELIST_FOR_CLIENT",
    ]),
    openResource(resource) {
      window.open(resource, "_blank");
    },
    pushAnalyticsEventToUmami: function (eventName, parameters) {
      if (typeof window === "undefined") {
        return;
      }
      const canUseUmami =
        window.umami !== undefined && this.getClientConfig.umamiSettings?.canUseUmami;

      if (canUseUmami) {
        try {
          window.umami.track(eventName, parameters);
        } catch (e) {
          console.error(`failed to track ${eventName}: ${e}`);
        }
      }
    },
  },
  created: async function () {
    var clientname = "";
    if (this.getClientConfig.clientname) clientname = this.getClientConfig.clientname;
    var lang = "en";
    var routeParam = this.$route.params.lang;
    if (routeParam && routeParam.trim().length > 0) {
      lang = routeParam;
    }
    await this.SET_SELECTED_LANGUAGE(lang);
    if (clientname) {
      await this.SET_LANGUAGELIST_FOR_CLIENT(clientname);
      await loadLanguageAsync(lang, clientname);
      // Set title now that translations are loaded — the router guard
      // may have run before messages were available
      if (this.$route.meta?.titleI18nKey) {
        document.title = this.$t(this.$route.meta.titleI18nKey).trim();
      }
    }
    this.UPDATE_ACCESSIBILITY_CLASS_LIST();
    this.UPDATE_ACCESSIBILITY_IS_ACTIVE();
    document.addEventListener("cambium-accessibility-widget-state-update", (e) => {
      const eventData = {
        toolName: e.detail.toolName,
        toolState: JSON.stringify(e.detail.toolState ?? ""),
      };
      window.gtag("event", "accessibility_widget_tool_click", eventData);
      eventData.fpclientuser = window.client;
      this.pushAnalyticsEventToUmami("accessibility_widget_tool_click", eventData);
      this.UPDATE_ACCESSIBILITY_CLASS_LIST();
      this.UPDATE_ACCESSIBILITY_IS_ACTIVE();
    });

    eventBus.$on("schoolYearClick", (data) => {
      window.gtag("event", "school_year_click", { selectedSchoolYear: data });
      this.pushAnalyticsEventToUmami("school_year_click", {
        selectedSchoolYear: data,
        fpclientuser: window.client,
      });
    });
    eventBus.$on("ViewDetailedReportTab", (data) => {
      window.gtag("event", "ViewDetailedReportTab", { viewDetailedReportTabActiveTab: data });
      this.pushAnalyticsEventToUmami("ViewDetailedReportTab", {
        viewDetailedReportTabActiveTab: data,
        fpclientuser: window.client,
      });
    });
    eventBus.$on("downloadDetailedReportButtonClick", () => {
      window.gtag("event", "download_detailed_report_button_click", {
        downloadDetailedReportButton: window.client,
      });
      this.pushAnalyticsEventToUmami("download_detailed_report_button_click", {
        downloadDetailedReportButton: window.client,
        fpclientuser: window.client,
      });
    });
    eventBus.$on("glossaryButtonClick", () => {
      window.gtag("event", "glossary_button_click");
      this.pushAnalyticsEventToUmami("glossary_button_click", {
        fpclientuser: window.client,
      });
    });
    eventBus.$on("guideButtonClick", () => {
      window.gtag("event", "guide_button_click");
      this.pushAnalyticsEventToUmami("guide_button_click", {
        fpclientuser: window.client,
      });
    });
    eventBus.$on("resourcesButtonClick", () => {
      window.gtag("event", "resources_button_click");
      this.pushAnalyticsEventToUmami("resources_button_click", { fpclientuser: window.client });
    });
    eventBus.$on("subscribeButtonClick", () => {
      window.gtag("event", "subscribe_button_click");
      this.pushAnalyticsEventToUmami("subscribe_button_click", { fpclientuser: window.client });
    });
    eventBus.$on("downloadInboxDocumentButtonClick", (data) => {
      window.gtag("event", "download_inbox_document_button_click", { inboxDocumentType: data });
      this.pushAnalyticsEventToUmami("download_inbox_document_button_click", {
        inboxDocumentType: data,
        fpclientuser: window.client,
      });
    });
  },

  mounted() {
    this.$nextTick(() => {
      const widgetInertManagedAttr = "data-accessibility-widget-inert-managed";

      // Remove only the inert attributes that this logic has applied.
      const clearManagedInert = () => {
        document.querySelectorAll(`[${widgetInertManagedAttr}]`).forEach((el) => {
          el.removeAttribute("inert");
          el.removeAttribute(widgetInertManagedAttr);
        });
      };

      const markElementInert = (element) => {
        if (!element) return;
        element.setAttribute("inert", "");
        element.setAttribute(widgetInertManagedAttr, "");
      };

      // Walk from the active widget up to (but not including) document.body, inerting every
      // sibling subtree at each level. This single algorithm handles both cases:
      //  - Desktop: the widget is a direct child of <body>, so #app (its sibling) is inerted.
      //  - Mobile:  the widget is inside #app, so #app stays in the protected path and only
      //             the non-widget subtrees within #app (and within the header) are inerted.
      const toggleAppInert = () => {
        clearManagedInert();

        const widgetWindow = document.querySelector("cambium-accessibility-widget-window");
        if (!widgetWindow) return;

        const activeWidget = widgetWindow.closest("cambium-accessibility-widget");
        if (!activeWidget) return;

        let current = activeWidget;
        while (current && current !== document.body) {
          const parent = current.parentElement;
          if (!parent) break;
          Array.from(parent.children).forEach((sibling) => {
            if (sibling !== current) {
              markElementInert(sibling);
            }
          });
          current = parent;
        }
      };

      // Use a MutationObserver to detect when the accessibility widget dialog opens or closes.
      // The widget uses stopPropagation so events don't bubble — MutationObserver is the only
      // reliable way to detect when cambium-accessibility-widget-window is added or removed.
      const accessibilityWidgetObserver = new MutationObserver((mutations) => {
        const widgetWindowAffected = mutations.some((m) =>
          [...m.addedNodes, ...m.removedNodes].some(
            (n) => n.nodeName?.toLowerCase() === "cambium-accessibility-widget-window"
          )
        );
        if (widgetWindowAffected) {
          toggleAppInert();
        }
      });

      const observeWidgets = () => {
        document.querySelectorAll("cambium-accessibility-widget").forEach((widget) => {
          accessibilityWidgetObserver.observe(widget, { childList: true });
        });
      };

      // Clear any stale inert state left by a previously deployed implementation.
      clearManagedInert();
      observeWidgets();

      // The header clone widget is appended ~1 s after init via cambiumAccessibilityWidgetInitSecondary.
      // Re-query after a short delay so the clone is also observed.
      setTimeout(observeWidgets, 1500);
    });
  },
};
</script>

<template>
  <div :class="appclass">
    <LoadingOverlay />
    <Snackbar />
    <AutoLogout v-if="isAuthenticated" />
    <Header />
    <!-- SUSIE - available on all pages when authenticated -->
    <!-- Use v-if and bind to client config so that we know whether or not to init Susie
    Use v-show so element exists in DOM for SUSIE script to find -->
    <Susie v-if="isSurveyModuleEnabled" v-show="isAuthenticated" />

    <main class="main_wrapper" id="main-content">
      <router-view v-slot="{ Component }" :key="$route.fullPath">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>

    <div class="v-container footerWrapper main_wrapper">
      <footer>
        {{ translate("App.copy") }} {{ new Date().getFullYear() }}
        <span class="footerCAIText">{{ translate("App.write") }} </span>
        {{ translate("App.text") }} |
        <a
          v-if="getClientConfig.layout === 'full' && getClientConfig.clientname === 'texas'"
          href="#"
          @click="openResource(termsofUseTexas)"
        >
          {{ translate("App.TOUP") }}
          <span class="sr-only">{{ $t("LoginLinks.newwindow") }}</span>
        </a>

        <a v-else href="#" @click="openResource(termsofUse)">
          {{ translate("App.TOU") }}
          <span class="sr-only">{{ $t("LoginLinks.newwindow") }}</span>
        </a>
      </footer>
    </div>
  </div>
</template>

<style>
html,
body,
div,
span,
applet,
object,
iframe,
h1,
h2,
h3,
h4,
h5,
h6,
p,
blockquote,
pre,
a,
abbr,
acronym,
address,
big,
cite,
code,
del,
dfn,
em,
img,
ins,
kbd,
q,
s,
samp,
small,
strike,
strong,
sub,
sup,
tt,
var,
u,
center,
dl,
dt,
dd,
ol,
ul,
li,
fieldset,
form,
label,
legend,
table,
caption,
tbody,
tfoot,
thead,
tr,
th,
td,
article,
aside,
canvas,
details,
embed,
figure,
figcaption,
footer,
header,
hgroup,
menu,
nav,
output,
ruby,
section,
summary,
time,
mark,
audio,
video {
  border: 0;
  font: inherit;
  font-size: 100%;
  margin: 0;
  padding: 0;
  vertical-align: baseline;
}
html {
  height: 100vh;
}
/* 
html[lang="en"] #response_rubric span[xml\:lang] {
  display: none;
}
// add other lang specific css in future if requested(filter hrr images)
html[lang="es"]
  #response_rubric
  div[data-hrr-element-type="rubricBlock"]:has(span[xml\:lang])
  p:not(:has(span[xml\:lang="es-mx"])) {
  display: none;
}
*/
body {
  background: #fff;
  color: #797979;
  font-family: "Roboto", sans-serif;
  font-size: 16px;
  font-variant-ligatures: none;
  height: 100%;
}
button {
  cursor: pointer;
}

#app a {
  color: var(--bs-primary);
}
#app {
  background: #fff; /*for accessibility tools*/
  font-family: "Roboto", sans-serif;
  height: 100%;
  padding: 0 1em;
}
#app .p-component,
.p-component {
  font-family: "Roboto", sans-serif !important;
}
.loginpage {
  width: 100%;
  /* height: 100%; */
  flex-direction: column;
  display: flex;
  align-items: center;
  /* justify-content: center; */
}
.loginpage > .main_wrapper {
  width: 100%;
}
.loginpage .v-application__wrap {
  justify-content: center;
}
.hidebranding.acpage .v-application__wrap {
  justify-content: center;
}

.acrpage .v-application__wrap {
  justify-content: center;
}
.texas .loginpage.v-application .error--text,
.texas .loginpage .v-alert .v-btn--outlined .v-btn__content .v-icon,
.texas .loginpage .v-alert .v-btn--round .v-btn__content .v-icon {
  color: #fff !important;
  caret-color: #fff !important;
}

.v-main__wrap {
  display: flex;
}

.main_wrapper {
  padding: 0;
}

#app footer {
  color: #333;
  font-size: 0.75em;
  text-align: right;
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-end;
  margin: 0 auto;
  padding: 0.5em 0 20px;
  position: relative;
}
#app footer span {
  margin-left: 0.25em;
  margin-right: 0.25em;
}
#app footer a {
  margin-left: 0.5em;
}
#app .loginpage .v-container footer {
  border-top: none;
  max-width: 900px;
  padding: 0.5rem 20px 20px;
  /* width: 90%; */
}

.texas #app .loginpage .v-container footer {
  font-size: 0.594rem;
  max-width: 552px;
  padding: 0.5rem 0 0;
  width: 100%;
}
.texas #app .loginpage .v-container footer.second_footer {
  padding-bottom: 1em;
}
#app .hidebranding .v-container .CAIHeader {
  display: none;
}
#app .hidebranding .v-container footer {
  display: none;
}

#app .acpage .v-container footer,
#app .acrpage .v-container footer {
  border-top: 1px solid #b7b7b7;
  max-width: 700px;
  padding: 0.5rem 0 1.5rem;
}

#app .homepage .v-container footer {
  border-top: 1px solid #b7b7b7;
}

.footerWrapper {
  padding: 0 0 12px !important;
}
.footerCAIText {
  font-weight: bold;
}

.fade-enter-active,
.fade-leave-active {
  transition-duration: 0.3s;
  transition-property: opacity;
  transition-timing-function: ease;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}
.hide {
  display: none !important;
}
/* --------------------- */
/* Focus Indicator Style */
a:focus,
button:focus,
[tabindex="0"]:focus,
li[role="tab"]:focus,
.v-radio--is-focused .v-input--selection-controls__input,
.v-input--is-focused .v-text-field__slot,
.v-input--is-focused .v-select__slot {
  outline: 2px dashed #005e9c !important;
  outline-offset: 0 !important;
}
.v-input--selection-controls__ripple {
  display: none;
}
.p-inputswitch:focus,
.p-inputswitch-slider:focus,
.p-focus {
  outline: 2px dashed #005e9c !important;
}
/* --------------------- */
.btn_wrapper {
  align-items: center;
  display: flex;
  flex-flow: row nowrap;
  font-family: "Roboto", sans-serif;
}

#app main {
  flex: 0 0 auto;
}
main .v-container {
  padding-top: 0;
  padding-bottom: 0;
  padding: 0;
}
.v-container .v-container {
  padding: 0;
}
.v-container .v-container .row--dense > .v-col,
.v-container .v-container .row--dense > [class*="col-"] {
  padding: 0;
}
.v-container .v-container .flex.md6 {
  flex: 0 0 calc(50% - 1em);
  margin: 0 1em 1em 0;
}
.v-container .v-container .flex.md6:nth-child(even) {
  margin: 0 0 1em 1em;
}
.v-row {
  margin-right: 0 !important;
  margin-left: 0 !important;
}

:root {
  --bs-black: #000;
  --bs-white: #fff;
  --bs-text: #4b4b4b;
  --bs-primary: #005c99;
  --new-badge: #fed330;
  --blue-color-1: #4b7bec;
  --blue-color-2: #a4bcf5;
  --blue-color-3: #ecf1fd;
  --blue-color-4: #fafbfe;
  --chartreuse-color-1: #a3cb38;
  --chartreuse-color-2: #d1e59b;
  --chartreuse-color-3: #f6faea;
  --chartreuse-color-4: #fcfef8;
  --green-color-1: #26de81;
  --green-color-2: #92eec0;
  --green-color-3: #e9fcf2;
  --green-color-4: #f9fefb;
  --magenta-color-1: #a55eea;
  --magenta-color-2: #d2aef4;
  --magenta-color-3: #f6eefd;
  --magenta-color-4: #fbf9fe;
  --orange-color-1: #fd9644;
  --orange-color-2: #fecaa1;
  --orange-color-3: #fff4ec;
  --orange-color-4: #fffcfa;
  --pink-color-1: #cf6a87;
  --pink-color-2: #e7b4c3;
  --pink-color-3: #faf0f3;
  --pink-color-4: #fdf9fa;
  --purple-color-1: #735bcd;
  --purple-color-2: #b8ace6;
  --purple-color-3: #f1eefa;
  --purple-color-4: #fbfafe;
  --salmon-color-1: #ea8685;
  --salmon-color-2: #f4c2c2;
  --salmon-color-3: #fdf3f3;
  --salmon-color-4: #fffbfb;
  --seafoam-color-1: #55e6c1;
  --seafoam-color-2: #a9f2e0;
  --seafoam-color-3: #edfdf9;
  --seafoam-color-4: #f9fffd;
  --teal-color-1: #63cdda;
  --teal-color-2: #b0e6ec;
  --teal-color-3: #effafb;
  --teal-color-4: #fafefe;
  --forest_green-color-1: #008a52;
  --forest_green-color-2: #80c5a8;
  --forest_green-color-3: #e5f3ee;
  --forest_green-color-4: #f0f8f5;
  --brick-color-1: #d53f3f;
  --brick-color-2: #ea9f9f;
  --brick-color-3: #fbecec;
  --brick-color-4: #fbecec;
  --gold-color-1: #f0b80f;
  --gold-color-2: #f3c94b;
  --gold-color-3: #fcf2d1;
  --gold-color-4: #fffbf1;
  --fuchsia-color-1: #dd2279;
  --fuchsia-color-2: #ee90bc;
  --fuchsia-color-3: #fce9f2;
  --fuchsia-color-4: #fef8fb;
  --svg-icon-caret: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='10' viewBox='0 0 16 10' fill='none'%3E%3Cpath d='M8 10L0 1.89189L1.86667 0L8 6.21622L14.1333 0L16 1.89189L8 10Z' fill='%23000000'/%3E%3C/svg%3E");
  --svg-icon-download: url("data:image/svg+xml,%3C?xml version='1.0' encoding='UTF-8'?%3E%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M21,16.5v4.5H3v-4.5H0v4.5c0,.79.32,1.56.88,2.12.56.56,1.32.88,2.12.88h18c.79,0,1.56-.32,2.12-.88s.88-1.32.88-2.12v-4.5h-3ZM19.5,10.5l-2.11-2.11-3.89,3.87V0h-3v12.26l-3.89-3.87-2.11,2.11,7.5,7.5,7.5-7.5Z'/%3E%3C/svg%3E");
  --svg-icon-filled_arrow: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath d='M8 5v14l11-7L8 5z' fill='%23212121'%3E%3C/path%3E%3C/svg%3E");
  --svg-icon-glossary: url("data:image/svg+xml,%3Csvg width='25' height='24' viewBox='0 0 25 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M21.833 2.66802V21.334H3.167V2.66802H21.833ZM23.3 0.00102234H1.7C1.54241 0.00102234 1.38637 0.0320613 1.24078 0.0923669C1.09519 0.152673 0.962902 0.241064 0.851472 0.352494C0.740042 0.463925 0.65165 0.596211 0.591345 0.741802C0.531039 0.887393 0.5 1.04344 0.5 1.20102V22.801C0.520852 23.1124 0.653974 23.4057 0.874659 23.6264C1.09534 23.847 1.3886 23.9802 1.7 24.001H23.3C23.6029 23.9578 23.8837 23.8174 24.1 23.6011C24.3164 23.3847 24.4568 23.104 24.5 22.801V1.20102C24.4791 0.889624 24.346 0.596367 24.1253 0.375682C23.9047 0.154997 23.6114 0.0218739 23.3 0.00102234ZM11.167 5.33402H19.167V8.00102H11.167V5.33402ZM11.167 10.667H19.167V13.334H11.167V10.667ZM11.167 16H19.167V18.667H11.167V16ZM5.833 5.33402H8.5V8.00102H5.833V5.33402ZM5.833 10.667H8.5V13.334H5.833V10.667ZM5.833 16H8.5V18.667H5.833V16Z' fill='%23005C99'/%3E%3C/svg%3E");
  --svg-icon-guide: url("data:image/svg+xml,%3C?xml version='1.0' encoding='UTF-8'?%3E%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M16,23.71l-8-2.73L0,24V2.96L8,.29l8,2.73L24,0v21.04l-8,2.67ZM14.67,20.52V5.3l-5.33-1.82v15.22l5.33,1.82ZM17.33,20.52l4-1.3V3.8l-4,1.5v15.22ZM2.67,20.2l4-1.5V3.48l-4,1.3v15.41Z'/%3E%3C/svg%3E");
  --svg-icon-guide-tx: url("data:image/svg+xml,%3C?xml version='1.0' encoding='UTF-8'?%3E%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 29 24'%3E%3Cpath d='M26.18,0H2.62C1.18,0,0,1.2,0,2.67v18.67c0,1.47,1.18,2.67,2.62,2.67h23.56c1.44,0,2.62-1.2,2.62-2.67V2.67C28.8,1.2,27.62,0,26.18,0ZM26.18,17.33H2.62V2.67h23.56v14.67Z'/%3E%3Cpath d='M26.18,0H2.62C1.18,0,0,1.2,0,2.67v18.67c0,1.47,1.18,2.67,2.62,2.67h23.56c1.44,0,2.62-1.2,2.62-2.67V2.67C28.8,1.2,27.62,0,26.18,0ZM26.18,17.33H2.62V2.67h23.56v14.67Z'/%3E%3Cpath d='M19,10l-7.5,4.33V5.67l7.5,4.33Z'/%3E%3C/svg%3E");
  --svg-icon-home: url("data:image/svg+xml,%3C?xml version='1.0' encoding='UTF-8'?%3E%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 24'%3E%3Cpath d='M2.71,21.33h4v-8h8v8h4v-12L10.71,3.33l-8,6v12ZM.04,24V8L10.71,0l10.67,8v16h-9.33v-8h-2.67v8H.04Z'/%3E%3C/svg%3E");
  --svg-icon-inbox: url("data:image/svg+xml,%3C?xml version='1.0' encoding='UTF-8'?%3E%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 25 24'%3E%3Cpath d='M21.83,0H3.17C1.7,0,.5,1.2.5,2.67v18.67c0,1.47,1.2,2.67,2.67,2.67h18.67c1.47,0,2.67-1.2,2.67-2.67V2.67C24.5,1.2,23.3,0,21.83,0ZM21.83,21.33H3.17v-4h4.75c.92,1.59,2.63,2.67,4.6,2.67s3.67-1.08,4.6-2.67h4.72v4Z'/%3E%3C/svg%3E");
  --svg-icon-info: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'%3E%3Cpath d='M8 0C6.41775 0 4.87103 0.469192 3.55544 1.34824C2.23985 2.22729 1.21447 3.47672 0.608967 4.93853C0.00346625 6.40034 -0.15496 8.00887 0.153721 9.56072C0.462403 11.1126 1.22433 12.538 2.34315 13.6569C3.46197 14.7757 4.88743 15.5376 6.43928 15.8463C7.99113 16.155 9.59966 15.9965 11.0615 15.391C12.5233 14.7855 13.7727 13.7602 14.6518 12.4446C15.5308 11.129 16 9.58225 16 8C16 5.87827 15.1571 3.84344 13.6569 2.34315C12.1566 0.842855 10.1217 0 8 0ZM9 13H7V7H9V13ZM9 5H7V3H9V5Z' fill='%23000'/%3E%3C/svg%3E");
  --svg-icon-resources: url("data:image/svg+xml,%3C?xml version='1.0' encoding='UTF-8'?%3E%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 34 24'%3E%3Cpath d='M20,8.82v-2.54c.82-.35,1.66-.61,2.53-.79.9-.18,1.81-.27,2.72-.26.64,0,1.28.05,1.91.15.63.1,1.24.22,1.84.37v2.39c-1.2-.45-2.47-.68-3.75-.67-.92,0-1.85.12-2.74.36-.87.24-1.71.57-2.51.99ZM20,17.05v-2.55c.82-.35,1.66-.61,2.53-.78.9-.18,1.81-.27,2.72-.26.64,0,1.28.05,1.91.15.63.1,1.24.22,1.84.37v2.39c-1.2-.45-2.47-.68-3.75-.67-.92,0-1.84.11-2.74.34-.88.23-1.72.57-2.51,1.01h0ZM20,12.94v-2.54c.82-.35,1.66-.61,2.53-.78.9-.18,1.81-.27,2.72-.26.64,0,1.28.05,1.91.15.63.1,1.24.22,1.84.37v2.39c-1.2-.45-2.47-.68-3.75-.67-.92,0-1.85.12-2.74.36-.87.24-1.71.57-2.51.99h0ZM8.75,17.94c1.16,0,2.31.13,3.43.39,1.15.27,2.26.67,3.32,1.18V4.79c-1.02-.6-2.12-1.05-3.26-1.35-2.02-.53-4.12-.59-6.17-.19-.88.17-1.74.44-2.57.78v14.8c.85-.29,1.72-.52,2.61-.67.87-.15,1.76-.22,2.64-.22h0ZM18.5,19.51c1.06-.51,2.17-.9,3.32-1.18,1.12-.26,2.28-.4,3.43-.39.89,0,1.77.07,2.64.22.89.16,1.76.38,2.61.67V4.04c-.83-.35-1.69-.61-2.57-.78-2.04-.4-4.15-.34-6.17.19-1.14.3-2.24.75-3.26,1.34v14.73ZM17,23.93c-1.18-.93-2.49-1.68-3.9-2.21-1.39-.52-2.86-.79-4.35-.78-1.42.02-2.83.27-4.16.75-1.49.52-2.88,1.31-4.09,2.32V2.32c1.19-.79,2.5-1.37,3.88-1.72C5.8.21,7.27,0,8.75,0c2.9,0,5.75.77,8.25,2.24C19.5.77,22.35,0,25.25,0c1.48,0,2.95.21,4.37.6,1.38.35,2.69.93,3.88,1.72v21.68c-1.2-1.02-2.58-1.8-4.07-2.32-1.34-.48-2.76-.73-4.18-.75-1.49,0-2.96.26-4.35.78-1.41.53-2.72,1.27-3.9,2.21Z'/%3E%3C/svg%3E");
  --svg-icon-language: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'%3E%3Cpath d='M9 0C7.21997 0 5.47991 0.527841 3.99987 1.51677C2.51983 2.50571 1.36628 3.91131 0.685088 5.55585C0.00389957 7.20038 -0.17433 9.00998 0.172937 10.7558C0.520203 12.5016 1.37737 14.1053 2.63604 15.364C3.89472 16.6226 5.49836 17.4798 7.24419 17.8271C8.99002 18.1743 10.7996 17.9961 12.4442 17.3149C14.0887 16.6337 15.4943 15.4802 16.4832 14.0001C17.4722 12.5201 18 10.78 18 9C18 6.61305 17.0518 4.32387 15.364 2.63604C13.6761 0.948212 11.387 0 9 0ZM9 16.875C7.92881 16.8761 6.86872 16.658 5.885 16.234L9.985 11.623C10.0765 11.5199 10.1271 11.3868 10.127 11.249V9.563C10.1271 9.48894 10.1126 9.41559 10.0843 9.34716C10.056 9.27873 10.0145 9.21657 9.96205 9.16425C9.90963 9.11193 9.8474 9.07049 9.77892 9.0423C9.71044 9.01411 9.63706 8.99974 9.563 9C7.576 9 5.481 6.935 5.46 6.915C5.40754 6.86243 5.34518 6.82078 5.27652 6.79246C5.20786 6.76414 5.13427 6.74971 5.06 6.75H2.813C2.66386 6.75027 2.52092 6.8097 2.41555 6.91525C2.31018 7.02081 2.251 7.16386 2.251 7.313V10.688C2.25153 10.792 2.28088 10.8937 2.33578 10.982C2.39068 11.0703 2.46899 11.1416 2.562 11.188L4.501 12.158V15.458C2.97281 14.3929 1.87135 12.8208 1.39209 11.0207C0.912834 9.22066 1.08676 7.30901 1.883 5.625H3.938C4.01226 5.62524 4.08584 5.61078 4.15449 5.58246C4.22314 5.55414 4.28551 5.51252 4.338 5.46L6.588 3.21C6.64053 3.1575 6.68215 3.09513 6.71047 3.02648C6.73878 2.95783 6.75324 2.88426 6.753 2.81V1.452C8.64477 0.890638 10.6781 1.05759 12.453 1.92C12.38 1.982 12.309 2.046 12.241 2.114C11.769 2.58583 11.4474 3.18701 11.317 3.84155C11.1866 4.49609 11.2532 5.17461 11.5084 5.79131C11.7635 6.40802 12.1958 6.93522 12.7505 7.30629C13.3053 7.67735 13.9576 7.87561 14.625 7.876C14.6803 7.876 14.736 7.876 14.792 7.876C15.2927 10.0355 15.2418 12.2863 14.644 14.421C14.6365 14.4512 14.6314 14.482 14.629 14.513C13.896 15.2626 13.0203 15.8578 12.0535 16.2635C11.0867 16.6692 10.0484 16.8771 9 16.875Z' fill='%23005C99'/%3E%3C/svg%3E");
  --svg-icon-print: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='17' viewBox='0 0 18 17' fill='none'%3E%3Cpath d='M15.2991 4.69968H14.3988V0.200195H3.60021V4.69968H2.6999C1.98384 4.69968 1.29711 4.98413 0.790781 5.49046C0.284452 5.99679 0 6.68352 0 7.39957V12.7994H3.60021V16.3996H14.3998V12.7994H18V7.39957C18 7.04493 17.9301 6.69376 17.7944 6.36613C17.6586 6.03849 17.4597 5.74081 17.2089 5.49009C16.958 5.23937 16.6603 5.04052 16.3326 4.9049C16.0049 4.76927 15.6537 4.69954 15.2991 4.69968ZM5.39979 1.99978H12.5992V4.69968H5.39979V1.99978ZM12.5992 12.7994V14.5989H5.39979V10.9998H12.5992V12.7994ZM14.3998 10.9998V9.2002H3.60021V10.9998H1.79958V7.39957C1.80041 7.16114 1.89556 6.93273 2.06425 6.76423C2.23294 6.59573 2.46147 6.50085 2.6999 6.5003H15.2991C15.5377 6.50085 15.7664 6.59588 15.9351 6.7646C16.1038 6.93332 16.1988 7.162 16.1994 7.40061V10.9998H14.3998ZM14.3998 6.94994C14.5779 6.94994 14.7519 7.00274 14.9 7.10167C15.048 7.20059 15.1634 7.3412 15.2316 7.50571C15.2997 7.67022 15.3175 7.85125 15.2828 8.02589C15.2481 8.20053 15.1623 8.36095 15.0364 8.48686C14.9105 8.61277 14.7501 8.69852 14.5754 8.73326C14.4008 8.768 14.2198 8.75017 14.0553 8.68203C13.8907 8.61388 13.7501 8.49849 13.6512 8.35043C13.5523 8.20238 13.4995 8.02831 13.4995 7.85025C13.5 7.61164 13.5951 7.38296 13.7638 7.21424C13.9325 7.04552 14.1612 6.95048 14.3998 6.94994Z' fill='%23005C99'/%3E%3C/svg%3E");
  --svg-icon-report: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='31' height='25' viewBox='0 0 31 25' fill='none'%3E%3Cpath d='M27.1246 4.50208V0.502075H-0.00036249V22.5021C-0.00446566 22.7607 0.0424108 23.0175 0.13759 23.258C0.232769 23.4984 0.374385 23.7178 0.554351 23.9035C0.734318 24.0892 0.949108 24.2377 1.18645 24.3404C1.4238 24.4431 1.67906 24.498 1.93764 24.5021H28.0936C28.8768 24.4894 29.6228 24.1663 30.1678 23.6037C30.7127 23.0412 31.0119 22.2852 30.9996 21.5021V4.50208H27.1246ZM25.1866 22.5021H1.93764V2.50208H25.1876L25.1866 22.5021ZM3.87464 6.50208H23.2496V8.50208H3.87464V6.50208ZM15.4996 10.5021H23.2496V12.5021H15.4996V10.5021ZM15.4996 14.5021H23.2496V16.5021H15.4996V14.5021ZM15.4996 18.5021H21.3126V20.5021H15.4996V18.5021ZM3.87464 10.5021H13.5626V20.5021H3.87464V10.5021Z' fill='%23005C99'/%3E%3C/svg%3E");
  --svg-icon-sign_out: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'%3E%3Cpath d='M14.0002 5L12.5902 6.41L14.1702 8H6.00008V10H14.1702L12.5902 11.58L14.0002 13L18.0002 9L14.0002 5ZM2.00003 2H9.00012V0H2.00003C1.47007 0.00158273 0.962278 0.212805 0.587543 0.587535C0.212807 0.962265 0.00158275 1.47005 0 2V16C0.00158275 16.5299 0.212807 17.0377 0.587543 17.4125C0.962278 17.7872 1.47007 17.9984 2.00003 18H9.00012V16H2.00003V2Z' fill='%23005C99'/%3E%3C/svg%3E");
}

.svg-icon {
  background-color: var(--bs-primary);
  content: "";
  height: 24px;
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-size: contain;
  mask-size: contain;
  place-self: center;
  width: 24px;
}
.svg-icon-download {
  -webkit-mask-image: var(--svg-icon-download);
  mask-image: var(--svg-icon-download);
}
.svg-icon-report {
  -webkit-mask-image: var(--svg-icon-report);
  mask-image: var(--svg-icon-report);
  width: 31px;
}
.svg-icon-caret {
  display: block;
  height: 1rem;
  -webkit-mask-image: var(--svg-icon-caret);
  mask-image: var(--svg-icon-caret);
  transition: all 0.35s linear;
  width: 1rem;
}
.pi-sort-down-fill:before,
.pi-sort-up-fill:before {
  background-color: #000;
  content: "";
  display: block;
  -webkit-mask-image: var(--svg-icon-caret);
  mask-image: var(--svg-icon-caret);
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-size: contain;
  mask-size: contain;
  place-self: center;
  transition: all 0.35s linear;
  width: 16px;
}
.pi-sort-up-fill:before {
  transform: rotate(180deg);
}

.new {
  background: #fed330;
  border-radius: 5px;
  color: #000;
  font-size: 1rem;
  font-weight: bold;
  margin-right: 0.75rem;
  padding: 0.25rem 0.75rem;
}
:root[dir="rtl"] .new {
  margin-left: 0.75rem;
  margin-right: 0;
}

.noScroll {
  overflow: hidden;
}

.subject_icon {
  align-items: center;
  background: #fff;
  border: none;
  border-radius: 50%;
  display: flex;
  height: 45px;
  justify-content: center;
  left: 7px;
  margin: 0;
  position: absolute;
  top: initial;
  width: 45px;
}
.subject_icon img {
  height: auto;
  max-width: 50px;
}

#page_header h1 .year_select > .v-input__control > .v-input__slot {
  margin: 0 !important;
}
#page_header h1 .v-select__selections {
  line-height: 1em;
}
#page_header h1 .v-select__selection--comma {
  color: #1b4679;
  margin: 0;
}
#page_header .v-select__selection--comma {
  color: #1b4679;
  margin: 0;
}
#page_header .v-input__slot:before {
  border-color: #1b4679;
}
/* .page_info:deep(.p-dropdown) {
  border: none !important;
} */
@font-face {
  font-family: "family_portal";
  src: url("./assets/styles/fonts/family_portal.eot");
  src: url("./assets/styles/fonts/family_portal.eot") format("embedded-opentype"),
    url("./assets/styles/fonts/family_portal.ttf") format("truetype"),
    url("./assets/styles/fonts/family_portal.woff") format("woff"),
    url("./assets/styles/fonts/family_portal.svg") format("svg");
  font-weight: normal;
  font-style: normal;
  font-display: block;
}

[class^="icon-"],
[class*=" icon-"] {
  /* use !important to prevent issues with browser extensions that change ./assets/styles/./assets/styles/fonts */
  font-family: "family_portal" !important;
  speak: none;
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  line-height: 1;
  /* Better Font Rendering =========== */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.icon-display:before {
  content: "\e90a";
}

.icon-school:before {
  content: "\e90b";
}

.icon-shield:before {
  content: "\e90c";
}

.icon-download_arrow:before {
  content: "\e907";
}

.icon-info:before {
  content: "\e916";
}

.icon-document:before {
  content: "\e908";
}

.icon-close:before {
  content: "\e909";
}

.icon-down_arrow:before {
  content: "\e906";
}

.icon-up_arrow:before {
  content: "\e918";
}

.icon-correct:before {
  content: "\e91a";
}

.icon-correct {
  color: #0d7f40;
}

.icon-incorrect:before {
  content: "\e919";
}

.icon-previous:before {
  content: "\e90d";
}

.icon-next:before {
  content: "\e90e";
}

.icon-logout:before {
  content: "\e900";
}

.icon-map:before {
  content: "\e901";
}

.icon-report:before {
  content: "\e902";
}

.icon-book:before {
  content: "\e903";
}

.icon-home:before {
  content: "\e904";
}

.icon-print:before {
  content: "\e905";
}

.icon-resources-apple:before {
  content: "\e912";
}
.icon-walkthrough:before {
  content: "\e901";
}
.texas .icon-walkthrough:before {
  content: "\e915";
}

.v-dialog.full_modal [role="dialog"] {
  height: calc(100% - 56px);
}
.v-dialog.full_modal {
  background: #fff;
  display: flex;
  flex-flow: column nowrap;
  height: calc(100% - 2em);
  position: relative;
}
.v-dialog.full_modal header {
  background-color: #fff !important;
  border-bottom: 1px solid #90b4ce;
  box-shadow: none;
  color: #2b2f31;
  flex: 0;
  height: auto !important;
}
.v-dialog.full_modal header .v-toolbar__content {
  box-sizing: border-box;
  height: 42px !important;
}
.v-dialog.full_modal header .v-toolbar__content .v-toolbar__title {
  color: #2b2f31;
  font-weight: 700;
}
.v-dialog.full_modal iframe {
  flex: 1;
}
.v-dialog.full_modal .full_modal_close {
  color: #2b2f31 !important;
  position: absolute;
  right: 0.5em;
  top: 0.25em;
}

.sr-only {
  position: absolute;
  left: -10000px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
}
:root[dir="rtl"] .sr-only {
  left: inherit;
  right: -10000px;
}
:root[dir="rtl"] .text-left {
  text-align: right;
}

@media screen and (min-width: 1264px) {
  #app .main_wrapper {
    max-width: 1264px;
    margin: auto;
  }
  #app .loginpage .main_wrapper {
    margin: initial;
  }
  .texas #app {
    padding: 0;
  }
}

@media screen and (min-width: 1900px) {
  #app .main_wrapper {
    max-width: 1785px;
  }
}

@media screen and (max-width: 1024px) {
  .v-container {
    max-width: 100% !important;
    padding: 0;
  }
  /* .main_wrapper {
    padding: 0 1em !important;
  } */
}
@media only screen and (max-width: 610px) {
  .texas #app .loginpage .main_wrapper .grid {
    margin-left: 0;
    margin-right: 0;
  }
}
@media screen and (max-width: 900px) {
  /* #app {
    font-size: 90%;
  } */
  .v-container .v-container .flex.md6 {
    flex: 0 0 100%;
    margin: 0 0 1em;
  }
  .v-container .v-container .flex.md6:nth-child(even) {
    margin: 0 0 1em;
  }
}
.v-dialog.full_modal {
  background: #fff;
  display: flex;
  flex-flow: column nowrap;
  height: calc(100% - 2em);
}

#language_picker .v-input__control {
  width: 125px;
}
#language_picker .v-input__control .v-input__slot {
  margin: 0;
}
#language_picker .v-input__control .v-input__slot legend {
  padding: 0;
  text-align: right;
}
#language_picker .v-input__control .v-input__slot .v-card {
  left: 20px;
  padding: 0.5em;
  position: absolute;
  top: 26px;
  width: 100%;
  z-index: 1;
}
#language_picker .v-input__control .v-input__slot .v-card .v-radio {
  border-top: 1px solid #ccc;
  padding-top: 0.5em;
}
#language_picker .v-input__control .v-input__slot .v-card .v-radio:first-child {
  border-top: none;
  padding-top: 0;
}
#language_picker .v-input__control .v-messages {
  display: none;
}
/* .loginpage #language_picker {
  margin-top: 38px;
} */
/* .texas .loginpage #language_picker {
  margin-top: 0;
  position: absolute;
  right: 0;
  top: -40px;
} */

.hawaii .loginpage #language_picker {
  display: none;
}

.hawaii .CAIHeader #language_picker {
  display: none;
}

/* Language Dropdown */
#language_picker .p-dropdown {
  background: none;
  border: none;
}
/* .loginpage #language_picker .p-dropdown {
  margin-bottom: 0.25rem;
} */
/* #language_picker .p-dropdown-label {
  color: var(--bs-primary);
  font-size: 0.875rem;
  padding-left: 1.75rem;
  position: relative;
} */
#language_picker .p-dropdown .p-dropdown-trigger {
  display: none;
}
/* #language_picker .p-dropdown-label::before {
  content: "";
  background-color: var(--bs-primary);
  -webkit-mask-image: var(--svg-icon-language);
  mask-image: var(--svg-icon-language);
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-size: cover;
  mask-size: cover;
  width: 1rem;
  height: 1rem;
  position: absolute;
  left: 0.4rem;
} */
#language_picker .p-dropdown:not(.p-disabled).p-focus {
  box-shadow: none;
}
#language_picker .p-dropdown-panel .p-dropdown-items {
  padding: 1rem 0;
}
#language_picker .p-dropdown-panel .p-dropdown-items .p-dropdown-item {
  font-size: 0.875rem;
  padding: 0.5rem 1.5rem;
}
#language_picker .p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight {
  color: #fff;
  background: var(--bs-primary);
}
#language_picker .p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-focus {
  outline-offset: -2px !important;
}
#language_picker .p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight.p-focus {
  outline: 2px dashed #fff !important;
  outline-offset: -3px !important;
}

.texas .loginpage .subscribe,
.acpage .subscribe,
.acrpage .subscribe {
  display: none;
}

.v-application--is-ltr .v-text-field .v-label {
  transform-origin: 0% -100% !important;
}
/* Header */

.CAIHeader {
  align-items: flex-end;
  color: #005e9e;
  display: flex;
  justify-content: space-between;
}
.CAIHeader .branding {
  display: flex;
  align-items: center;
}
.CAIHeader .logo {
  background: url("./assets/images/CA-Logo_CLR_Horz.svg") no-repeat;
  display: inline-block;
  height: 35px;
  margin-right: 0;
  padding: 0;
  text-decoration: none;
  width: 115px;
}
.CAIHeader .logo:hover {
  color: inherit;
  text-decoration: none;
}
.CAIHeader .logo.markonly {
  background: url("./assets/images/CA-Logo_CLR_Mark.svg") no-repeat;
  padding: 0.5em 0 0 50px;
}
.CAIText {
  font-size: 0.688em;
  /*11px*/
  font-weight: 400;
  line-height: 10px;
  text-align: center;
}
.CAIText span {
  color: #2c9ed9;
}
.CAIHeader .systemName {
  border-left: 1px solid #005e9e;
  font-size: 2.125em;
  font-weight: 300;
  margin: 0 0 0 0.5em;
  padding: 0 0 0 0.5em;
}
:root[dir="rtl"] .CAIHeader .systemName {
  border-left: none;
  border-right: 1px solid #005e9e;
  margin: 0 0.5em 0 0;
  padding: 0 0.5em 0 0;
}
/* .CAIHeader .userbar {
  margin: 0;
  padding: 0;
  font-size: 0.875em;
  line-height: 18px;
  text-align: right;
} */

/* Footer */

footer {
  /* border-top: 1px solid #b7b7b7; */
  color: #333;
  font-size: 0.75em;
  margin: 0;
  padding: 0.5em 0;
  text-align: right;
}
#login_screen footer,
.subject footer,
.test footer {
  border-top: none;
}
.footerCAIText {
  font-weight: bold;
}

/* Body */

#mainWrapper {
  margin: 0 auto;
  position: relative;
  width: 100%;
}
@media only screen and (min-width: 1280px) {
  #mainWrapper {
    width: 1280px;
  }
}

/* Student Bar */

/* #student_bar {
  background: #005e9c;
  border-radius: 10px;
  display: flex;
  flex-flow: column nowrap;
  height: 70px;
  justify-content: space-between;
  margin: 0.5em 0;
  padding: 0.65em 1em;
} */
#top_bar {
  align-items: stretch;
  display: flex;
  margin: 0.625rem 0;
}
#top_bar button {
  background: none;
  border: none;
  border-radius: 0.25rem;
  flex: 0 0 auto;
  margin: 0 4px;
  padding: 0.25rem 0.5rem;
  position: relative;
}
#top_bar button:not(:last-child)::after {
  background: #8faacb;
  content: "";
  height: 100%;
  position: absolute;
  right: -4px;
  top: 0;
  width: 1px;
}
:root[dir="rtl"] #top_bar button::after {
  left: -4px;
  right: inherit;
}
#top_bar button .btn_wrapper {
  color: var(--bs-primary);
  display: flex;
  flex-flow: column nowrap;
  font-size: 0.688rem;
  line-height: 1;
}
#top_bar:not(:has(.page_buttons_inbox)) .page_buttons_container button .btn_wrapper {
  flex-flow: row nowrap;
}
:root[lang="my"]
  #top_bar:not(:has(.page_buttons_inbox))
  .page_buttons_container
  button
  .btn_wrapper,
:root[lang="vi"]
  #top_bar:not(:has(.page_buttons_inbox))
  .page_buttons_container
  button
  .btn_wrapper {
  flex-flow: column nowrap;
}
#top_bar button .btn_wrapper .svg-icon {
  margin-bottom: 0.375rem;
}
#top_bar:not(:has(.page_buttons_inbox)) .page_buttons_container button .btn_wrapper .svg-icon {
  margin: 0 0.375rem 0 0;
}
:root[dir="rtl"]
  #top_bar:not(:has(.page_buttons_inbox))
  .page_buttons_container
  button
  .btn_wrapper
  .svg-icon {
  margin: 0 0 0 0.375rem;
}
:root[lang="my"]
  #top_bar:not(:has(.page_buttons_inbox))
  .page_buttons_container
  button
  .btn_wrapper
  .svg-icon,
:root[lang="vi"]
  #top_bar:not(:has(.page_buttons_inbox))
  .page_buttons_container
  button
  .btn_wrapper
  .svg-icon {
  margin: 0 0 0.375rem;
}

/* Page Header */

#page_header {
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  margin: 1rem 0 0.375rem;
  position: relative;
}
.subject #page_header {
  margin: 1em 0 0.25em;
}
#page_header .page_info {
  display: flex;
  flex: 1 0 0;
  flex-flow: column nowrap;
  /* margin-right: 1em; */
}
#page_header .page_info nav {
  color: #333;
  margin: 0 0 0.25em;
}
#page_header .page_info nav a {
  text-decoration: underline;
}
/* FAM-2728 01/22/26*/
#page_header .page_info > div div:not(.sort_note):last-child {
  color: #595959;
}
.dashboard #page_header .page_info nav {
  display: none;
}
/* #page_header h1 {
  color: #000;
  display: flex;
  flex-flow: row wrap;
  font-size: 1.875em;
} */
/* #page_header h1 .bold {
  font-weight: bold;
} */
#page_header h1 .btn-group {
  margin-left: 0.35em;
}
#page_header h1 .btn-group button,
#page_header h1 .btn-group .multiselect {
  border: none;
  border-bottom: 1px solid #1b4679;
  border-radius: 0;
  color: #1b4679;
  font-size: 1em;
  max-width: inherit;
  min-width: inherit;
  padding: 0;
}
#page_header h1 .btn-group .multiselect .caret {
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-top: 10px solid;
  color: #1b4679;
  margin-left: 10px;
  margin-top: 14px;
}
#page_header h1 .multiselect-container.dropdown-menu {
  border-color: #1b4679;
  border-radius: 0;
  top: calc(100% - 2px);
  width: 100%;
}
#page_header h1 .multiselect-container > li > a {
  padding: 0.25em;
}
#page_header h1 .dropdown-menu > li > a {
  font-size: 1.25em;
}
.select-style {
  align-items: center;
  border-bottom: 1px solid #1b4679;
  border-radius: 0;
  display: flex;
  font-size: 1em;
  max-width: inherit;
  min-width: inherit;
  position: relative;
}

/* .select-style::before {
    color: #1B4679;
    font-size: 0.5em;
    position: absolute;
    right: 0;
    top: 0.75em;
    z-index: 1;
} */

.select-style select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background: none;
  border: none;
  box-shadow: none;
  color: #1b4679;
  cursor: pointer;
  font-size: 1em;
  max-width: 100%;
  min-width: 100%;
  padding: 0 0.75em 0 0;
  position: relative;
  z-index: 1;
}
.select-style .icon {
  font-size: 0.5em;
  margin-left: -1em;
}
#page_buttons {
  display: flex;
  flex-flow: row nowrap;
}
#page_buttons button {
  background: #e7f2fa;
  border: 1px solid #8faacb;
  border-right: none;
  color: #000;
  font-size: 0.625em;
  height: 50px;
  width: 56px;
}
#page_buttons button:first-child,
.dashboard #page_buttons #btn_glossary {
  border-radius: 10px 0 0 10px;
}
#page_buttons button:last-child {
  border-radius: 0 10px 10px 0;
  border-right: 1px solid #8faacb;
}
#page_buttons .btn_wrapper {
  flex-flow: column nowrap;
}
#page_buttons .btn_wrapper .icon {
  color: #1b4679;
  font-size: 2.4em;
  padding: 4px;
}
#page_header .note {
  color: #333;
  flex: 1 1 100%;
  font-size: 0.75em;
}
#page_header label {
  color: #333;
  margin-right: 0.5em;
}
#page_header .sorted_text {
  color: #333;
  display: flex;
  flex: 1 1 100%;
  flex-flow: row nowrap;
  margin: 0.4em 0 0;
}
#page_header .dashboard_options {
  display: flex;
  flex: 1 1 100%;
  flex-flow: row nowrap;
  margin: 0.5em 0 0;
}
#page_header .dashboard_options .subject_filter,
#page_header .dashboard_options .all_tests_toggle {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  flex-flow: row nowrap;
  margin: 0 1em 0 0;
}

.filter_row .mdi-menu-down {
  margin: 0 !important;
}
.v-select.v-input--is-dirty input {
  display: none;
}

.v-select__selection {
  max-width: none;
}
.v-application .green {
  background-color: #00ffffff !important;
}

#global_resources .p-dialog {
  background: #f2f9fc;
  max-height: 55%;
  max-width: 30%;
}
@media screen and (max-width: 500px) {
  #global_resources .p-dialog {
    max-width: 100%;
  }
  .topContainer {
    width: 100% !important;
  }
}
@media screen and (min-width: 501px) and (max-width: 1280px) {
  #global_resources .p-dialog {
    max-width: 50%;
  }
}
@media screen and (max-height: 700px) {
  .p-dialog,
  #global_resources .p-dialog {
    max-height: calc(100% - 4rem);
    margin-top: 4rem;
  }
}
@media screen and (min-width: 701px) and (max-height: 800px) {
  #global_resources .p-dialog {
    max-height: 80%;
  }
}
.p-dialog .p-dialog-header {
  border-bottom: 1px solid #8faacb !important;
  padding: 0.75rem 1rem !important;
}
#global_resources .p-dialog .p-dialog-header {
  border-bottom: none !important;
}
.p-dialog .p-dialog-content {
  padding: 1rem !important;
}
#global_resources .p-dialog .p-dialog-content .dialog_container {
  height: 100%;
  overflow: hidden;
}
.p-dialog .full_modal_close {
  color: #004a64 !important;
  position: absolute;
  right: 0.5rem;
  top: 0.25rem;
}
:root[dir="rtl"] .p-dialog .full_modal_close {
  left: 0.5rem;
  right: inherit;
}
.p-dialog .p-dialog-header .p-dialog-title {
  font-size: 1.25rem !important;
}
@media screen and (max-width: 767px) {
  .p-dialog .p-dialog-header .p-dialog-title {
    flex: 0 1 calc(100% - 25px);
  }
}

.texas .loginpage .v-row.v-row {
  margin-top: -1em;
}
.texas .loginpage .v-row.v-row .v-col {
  padding-left: 0;
  padding-right: 0;
}
.texas #app .loginpage #mainWrapper a {
  color: #fff;
  text-underline-offset: 2px;
}
#app .loginpage a:focus,
#app .loginpage button:focus,
#app .loginpage .p-inputtext:enabled:focus,
#app .loginpage .p-dropdown.p-component.p-inputwrapper.p-focus {
  border-color: inherit;
  border-radius: 2px;
  box-shadow: none;
  outline: 2px dashed #005e9c !important;
  outline-offset: 2px !important;
}
#app .loginpage .p-dropdown.p-component.p-inputwrapper.p-focus > .p-dropdown-label.p-inputtext,
.p-dropdown.p-component.p-inputwrapper.p-focus > .p-dropdown-label.p-inputtext {
  border-radius: inherit;
  outline: none !important;
}
.texas #app .loginpage .branding a:focus,
.texas #app .loginpage #mainWrapper a:focus,
.texas #app .loginpage #mainWrapper button:focus,
.texas #app .loginpage #mainWrapper .p-inputtext:enabled:focus,
.texas #app .loginpage #mainWrapper .p-dropdown.p-component.p-inputwrapper.p-focus {
  outline: 2px dashed #fff !important;
}
.texas .loginpage .v-text-field input {
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 0.5em;
}
.texas .loginpage .v-select > .v-input__control > .v-input__slot {
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 4px;
}
.texas .loginpage .v-select > .v-input__control > .v-input__slot input {
  background: none;
  border: none;
  border-radius: 0;
}
.texas .loginpage .v-select__selections {
  padding-left: 0.5em;
}
.texas .loginpage .theme--light.v-text-field > .v-input__control > .v-input__slot:before {
  border-color: rgba(0, 0, 0, 0);
}
.texas .loginpage label.v-label.theme--light {
  margin-left: 0.5em;
}
/* .texas .loginpage label.primary--text.v-label--active,
.texas .loginpage label.theme--light.v-label {
  color: #fff !important;
  transform: translateY(-22px) scale(0.75);
} */
.texas .loginpage label.theme--light.primary--text.v-label--active,
.texas .loginpage label.v-label.v-label--active.theme--light {
  color: #fff !important;
  margin-left: 0;
  transform: translateY(-22px) scale(0.75);
  display: none;
}
.texas .v-application.loginpage .primary {
  background-color: #00486e !important;
  border-color: #00486e !important;
  color: #fff;
  margin-top: 0.75em;
}
.texas .loginpage .v-list-item {
  min-height: 20px !important;
}
.texas .loginpage .v-list-item__content {
  padding: 4px 0;
}

.mobile_tabs {
  display: none !important;
}
.mobile_tabs {
  /* border-right: 1px solid;
  border-left: 1px solid; */
  border: 1px solid;
  display: block;
}
.mobile_tabs.mobile_subjects {
  border: none;
  border-radius: 10px 10px 0 0;
}
.mobile_tabs .v-text-field__details {
  display: none;
}
/* Colors */
.cblue.mobile_tabs {
  background-color: var(--blue-color-3) !important;
  border-color: var(--blue-color-2);
}
.cblue.mobile_tabs .p-dropdown {
  border-color: var(--blue-color-1);
}
.cblue.mobile_tabs.mobile_subjects {
  background-color: var(--blue-color-2) !important;
}
.cchartreuse.mobile_tabs {
  background-color: var(--chartreuse-color-3) !important;
  border-color: var(--chartreuse-color-2);
}
.cchartreuse.mobile_tabs .p-dropdown {
  border-color: var(--chartreuse-color-1);
}
.cchartreuse.mobile_tabs.mobile_subjects {
  background-color: var(--chartreuse-color-2) !important;
}
.cgreen.mobile_tabs {
  background-color: var(--green-color-3) !important;
  border-color: var(--green-color-2);
}
.cgreen.mobile_tabs .p-dropdown {
  border-color: var(--green-color-1);
}
.cgreen.mobile_tabs.mobile_subjects {
  background-color: var(--green-color-2) !important;
}
.cmagenta.mobile_tabs {
  background-color: var(--magenta-color-3) !important;
  border-color: var(--magenta-color-2);
}
.cmagenta.mobile_tabs .p-dropdown {
  border-color: var(--magenta-color-1);
}
.cmagenta.mobile_tabs.mobile_subjects {
  background-color: var(--magenta-color-2) !important;
}
.corange.mobile_tabs {
  background-color: var(--orange-color-3) !important;
  border-color: var(--orange-color-2);
}
.corange.mobile_tabs .p-dropdown {
  border-color: var(--orange-color-1);
}
.corange.mobile_tabs.mobile_subjects {
  background-color: var(--orange-color-2) !important;
}
.cpink.mobile_tabs {
  background-color: var(--pink-color-3) !important;
  border-color: var(--pink-color-2);
}
.cpink.mobile_tabs .p-dropdown {
  border-color: var(--pink-color-1);
}
.cpink.mobile_tabs.mobile_subjects {
  background-color: var(--pink-color-2) !important;
}
.cpurple.mobile_tabs {
  background-color: var(--purple-color-3) !important;
  border-color: var(--purple-color-2);
}
.cpurple.mobile_tabs .p-dropdown {
  border-color: var(--purple-color-1);
}
.cpurple.mobile_tabs.mobile_subjects {
  background-color: var(--purple-color-2) !important;
}
.csalmon.mobile_tabs {
  background-color: var(--salmon-color-3) !important;
  border-color: var(--salmon-color-2);
}
.csalmon.mobile_tabs .p-dropdown {
  border-color: var(--salmon-color-1);
}
.csalmon.mobile_tabs.mobile_subjects {
  background-color: var(--salmon-color-2) !important;
}
.cseafoam.mobile_tabs {
  background-color: var(--seafoam-color-3) !important;
  border-color: var(--seafoam-color-2);
}
.cseafoam.mobile_tabs .p-dropdown {
  border-color: var(--seafoam-color-1);
}
.cseafoam.mobile_tabs.mobile_subjects {
  background-color: var(--seafoam-color-2) !important;
}
.cteal.mobile_tabs {
  background-color: var(--teal-color-3) !important;
  border-color: var(--teal-color-2);
}
.cteal.mobile_tabs .p-dropdown {
  border-color: var(--teal-color-1);
}
.cteal.mobile_tabs.mobile_subjects {
  background-color: var(--teal-color-2) !important;
}
.cforest_green.mobile_tabs {
  background-color: var(--forest_green-color-3) !important;
  border-color: var(--forest_green-color-2);
}
.cforest_green.mobile_tabs .p-dropdown {
  border-color: var(--forest_green-color-1);
}
.cforest_green.mobile_tabs.mobile_subjects {
  background-color: var(--forest_green-color-2) !important;
}
.cgold.mobile_tabs {
  background-color: var(--gold-color-3) !important;
  border-color: var(--gold-color-2);
}
.cgold.mobile_tabs .p-dropdown {
  border-color: var(--gold-color-1);
}
.cgold.mobile_tabs.mobile_subjects {
  background-color: var(--gold-color-2) !important;
}
.cbrick.mobile_tabs {
  background-color: var(--brick-color-3) !important;
  border-color: var(--brick-color-2);
}
.cbrick.mobile_tabs .p-dropdown {
  border-color: var(--brick-color-1);
}
.cbrick.mobile_tabs.mobile_subjects {
  background-color: var(--brick-color-2) !important;
}
.cbrick.mobile_tabs.mobile_subjects {
  background-color: var(--brick-color-2) !important;
}
.cfuchsia.mobile_tabs {
  background-color: var(--fuchsia-color-3) !important;
  border-color: var(--fuchsia-color-2);
}
.cfuchsia.mobile_tabs .p-dropdown {
  border-color: var(--fuchsia-color-1);
}
.cfuchsia.mobile_tabs.mobile_subjects {
  background-color: var(--fuchsia-color-2) !important;
}

sup {
  font-size: 0.6em;
  vertical-align: super;
}

/* Subject Icons */
.blue .subject_icon img,
.cblue .subject_icon img {
  filter: brightness(0.75) sepia(1) hue-rotate(180deg) saturate(2);
}
.chartreuse .subject_icon img,
.cchartreuse .subject_icon img {
  filter: sepia(39%) saturate(441%) hue-rotate(29deg) brightness(95%) contrast(88%);
}
.green .subject_icon img,
.cgreen .subject_icon img {
  filter: brightness(0.75) sepia(1) hue-rotate(105deg) saturate(2);
}
.magenta .subject_icon img,
.cmagenta .subject_icon img {
  filter: sepia(16%) saturate(1000%) hue-rotate(249deg) brightness(95%);
}
.orange .subject_icon img,
.corange .subject_icon img {
  filter: brightness(0.75) sepia(1) hue-rotate(353deg) saturate(2);
}
.pink .subject_icon img,
.cpink .subject_icon img {
  filter: sepia(32%) saturate(694%) hue-rotate(292deg) brightness(92%) contrast(92%);
}
.purple .subject_icon img,
.cpurple .subject_icon img {
  filter: sepia(60%) saturate(1500%) hue-rotate(193deg) brightness(90%) contrast(83%);
}
.salmon .subject_icon img,
.csalmon .subject_icon img {
  filter: sepia(14%) saturate(1538%) hue-rotate(314deg) brightness(105%) contrast(83%);
}
.seafoam .subject_icon img,
.cseafoam .subject_icon img {
  filter: sepia(40%) saturate(336%) hue-rotate(100deg) brightness(100%) contrast(80%);
}
.teal .subject_icon img,
.cteal .subject_icon img {
  filter: sepia(30%) saturate(705%) hue-rotate(139deg) brightness(100%) contrast(84%);
}
.forest_green .subject_icon img,
.cforest_green .subject_icon img {
  filter: sepia(0.12) saturate(6) hue-rotate(140deg) brightness(0.96) contrast(0.92);
}
.gold .subject_icon img,
.cgold .subject_icon img {
  filter: sepia(0.35) saturate(1.6) hue-rotate(25deg) brightness(0.98) contrast(0.98);
}
.brick .subject_icon img,
.cbrick .subject_icon img {
  filter: sepia(30%) saturate(120%) hue-rotate(-10deg) brightness(1.05) contrast(1);
}
.fuchsia .subject_icon img,
.cfuchsia .subject_icon img {
  filter: grayscale(1) sepia(1) saturate(1.4) hue-rotate(300deg) brightness(1) contrast(1);
}

.school_year_header {
  width: 100%;
}
.texas .school_year_header {
  border-top: none !important;
}

.school_year .v-expansion-panel-content__wrap {
  padding: 1em 0 0 !important;
}
.school_year_header .v-expansion-panel-header__icon {
  transform: scale(1.5);
}
.school_year_header .v-expansion-panel-header__icon .v-icon {
  color: #000 !important;
}

#more_notifications p {
  margin: 0 0 1rem;
}

.modal-backdrop {
  position: fixed;
  background-color: rgba(0, 0, 0, 0.25);
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9997;
  pointer-events: all !important;
}

/* Item Rationales */
#response_rubric div[data-hrr-element-type="rubricBlock"] {
  border-top: 1px solid #999;
  margin-top: 1rem;
  padding-top: 1rem;
}
#response_rubric div[data-score]::before {
  display: block;
  font-weight: 700;
  margin-bottom: 0.5rem;
}
#response_rubric div[data-score="4"]::before {
  content: "Score: 4";
}
#response_rubric div[data-score="3"]::before {
  content: "Score: 3";
}
#response_rubric div[data-score="2"]::before {
  content: "Score: 2";
}
#response_rubric div[data-score="1"]::before {
  content: "Score: 1";
}
#response_rubric div[data-hrr-element-type="rubricBlock"] strong {
  font-weight: 500;
}
#response_rubric div[data-hrr-element-type="rubricBlock"] ul {
  padding: 0 0 0 1rem;
}
#response_rubric div[data-hrr-element-type="rubricBlock"] ul li {
  margin: 0.5rem 0;
}
.texas #response_rubric div[data-score="1"]::before,
.texas #response_rubric div[data-score="2"]::before,
.texas #response_rubric div[data-score="3"]::before,
.texas #response_rubric div[data-score="4"]::before {
  display: none;
}
/*hrr*/
/* #response_rubric .lang_response_rubric span[xml\:lang] {
  display: none;
} */

#response_rubric
  .lang_response_rubric
  div[data-hrr-element-type="rubricBlock"]:has(span[xml\:lang])
  p:not(:has(span[xml\:lang="es-mx"])) {
  display: none;
}

/* Writing Dimension Table Content */
.writing_dimension_section .report_table tbody td ul {
  margin: 0.5rem 0 0 0.5rem;
}
.writing_dimension_section .report_table li {
  margin: 0.25rem 0 0 0.25rem;
}

.student-info-bar-dropdown-items {
  min-width: 200px !important;
}
:root[dir="rtl"] .p-multiselect-panel .p-multiselect-items .p-multiselect-item .p-checkbox,
:root[dir="rtl"] .p-multiselect-panel .p-multiselect-header .p-checkbox {
  margin-left: 0.5rem;
  margin-right: 0;
}

/* Login Form */
#dobMonth_list .p-dropdown-item,
#dobDay_list .p-dropdown-item,
#dobYear_list .p-dropdown-item,
#acl_dobMonth_list .p-dropdown-item,
#acl_dobDay_list .p-dropdown-item,
#acl_dobYear_list .p-dropdown-item {
  padding: 0.25rem 0.5rem !important;
}
.p-dropdown-items-wrapper {
  max-height: 380px !important;
}

/* Report Buttons */
.btn_detailed_report {
  background: #fff;
  border: 1px solid;
  border-radius: 0.625rem;
  filter: drop-shadow(0 0.25rem 0);
  color: var(--bs-primary);
  font-family: "Roboto", sans-serif;
  font-size: 0.75rem;
  justify-self: center;
  margin: 0 0 0.25rem;
  padding: 0;
  place-self: center;
  text-align: center;
  text-decoration: none;
  transition: all 0.1s linear;
}
.btn_detailed_report:hover {
  margin: 0.25rem 0 0;
}
/*Blue*/
.cblue .btn_detailed_report {
  border-color: var(--blue-color-1);
  -webkit-filter: drop-shadow(0px 4px 0px var(--blue-color-1));
  filter: drop-shadow(0px 4px 0px var(--blue-color-1));
}
.cblue .btn_detailed_report:hover {
  background: var(--blue-color-4);
  -webkit-filter: drop-shadow(0px 0px 0px var(--blue-color-1));
  filter: drop-shadow(0px 0px 0px var(--blue-color-1));
}

/* Chartreuse */
.cchartreuse .btn_detailed_report {
  border-color: var(--chartreuse-color-1);
  -webkit-filter: drop-shadow(0px 4px 0px var(--chartreuse-color-1));
  filter: drop-shadow(0px 4px 0px var(--chartreuse-color-1));
}
.cchartreuse .btn_detailed_report:hover {
  background: var(--chartreuse-color-4);
  -webkit-filter: drop-shadow(0px 0px 0px var(--chartreuse-color-1));
  filter: drop-shadow(0px 0px 0px var(--chartreuse-color-1));
}

/*Green*/
.cgreen .btn_detailed_report {
  border-color: var(--green-color-1);
  -webkit-filter: drop-shadow(0px 4px 0px var(--green-color-1));
  filter: drop-shadow(0px 4px 0px var(--green-color-1));
}
.cgreen .btn_detailed_report:hover {
  background: var(--green-color-4);
  -webkit-filter: drop-shadow(0px 0px 0px var(--green-color-1));
  filter: drop-shadow(0px 0px 0px var(--green-color-1));
}

/*Magenta*/
.cmagenta .btn_detailed_report {
  border-color: var(--magenta-color-1);
  -webkit-filter: drop-shadow(0px 4px 0px var(--magenta-color-1));
  filter: drop-shadow(0px 4px 0px var(--magenta-color-1));
}
.cmagenta .btn_detailed_report:hover {
  background: var(--magenta-color-4);
  -webkit-filter: drop-shadow(0px 0px 0px var(--magenta-color-1));
  filter: drop-shadow(0px 0px 0px var(--magenta-color-1));
}

/*Orange*/
.corange .btn_detailed_report {
  border-color: var(--orange-color-1);
  -webkit-filter: drop-shadow(0px 4px 0px var(--orange-color-1));
  filter: drop-shadow(0px 4px 0px var(--orange-color-1));
}
.corange .btn_detailed_report:hover {
  background: var(--orange-color-4);
  -webkit-filter: drop-shadow(0px 0px 0px var(--orange-color-1));
  filter: drop-shadow(0px 0px 0px var(--orange-color-1));
}

/*Pink*/
.cpink .btn_detailed_report {
  border-color: var(--pink-color-1);
  -webkit-filter: drop-shadow(0px 4px 0px var(--pink-color-1));
  filter: drop-shadow(0px 4px 0px var(--pink-color-1));
}
.cpink .btn_detailed_report:hover {
  background: var(--pink-color-4);
  -webkit-filter: drop-shadow(0px 0px 0px var(--pink-color-1));
  filter: drop-shadow(0px 0px 0px var(--pink-color-1));
}

/*Purple*/
.cpurple .btn_detailed_report {
  border-color: var(--purple-color-1);
  -webkit-filter: drop-shadow(0px 4px 0px var(--purple-color-1));
  filter: drop-shadow(0px 4px 0px var(--purple-color-1));
}
.cpurple .btn_detailed_report:hover {
  background: var(--purple-color-4);
  -webkit-filter: drop-shadow(0px 0px 0px var(--purple-color-1));
  filter: drop-shadow(0px 0px 0px var(--purple-color-1));
}

/*Salmon*/
.csalmon .btn_detailed_report {
  border-color: var(--salmon-color-1);
  -webkit-filter: drop-shadow(0px 4px 0px var(--salmon-color-1));
  filter: drop-shadow(0px 4px 0px var(--salmon-color-1));
}
.csalmon .btn_detailed_report:hover {
  background: var(--salmon-color-4);
  -webkit-filter: drop-shadow(0px 0px 0px var(--salmon-color-1));
  filter: drop-shadow(0px 0px 0px var(--salmon-color-1));
}

/*Seafoam*/
.cseafoam .btn_detailed_report {
  border-color: var(--seafoam-color-1);
  -webkit-filter: drop-shadow(0px 4px 0px var(--seafoam-color-1));
  filter: drop-shadow(0px 4px 0px var(--seafoam-color-1));
}
.cseafoam .btn_detailed_report:hover {
  background: var(--seafoam-color-4);
  -webkit-filter: drop-shadow(0px 0px 0px var(--seafoam-color-1));
  filter: drop-shadow(0px 0px 0px var(--seafoam-color-1));
}

/*Teal*/
.cteal .btn_detailed_report {
  border-color: var(--teal-color-1);
  -webkit-filter: drop-shadow(0px 4px 0px var(--teal-color-1));
  filter: drop-shadow(0px 4px 0px var(--teal-color-1));
}
.cteal .btn_detailed_report:hover {
  background: var(--teal-color-4);
  -webkit-filter: drop-shadow(0px 0px 0px var(--teal-color-1));
  filter: drop-shadow(0px 0px 0px var(--teal-color-1));
}
.btn_detailed_report .btn_wrapper {
  display: grid;
  grid-column-gap: 0.25rem;
  grid-template-columns: 31px 84px;
  line-height: 1.15;
  padding: 0.5rem 0.75rem;
}

/*Florida Green*/
.cforest_green .btn_detailed_report {
  border-color: var(--forest_green-color-1);
  -webkit-filter: drop-shadow(0px 4px 0px var(--forest_green-color-1));
  filter: drop-shadow(0px 4px 0px var(--forest_green-color-1));
}
.cforest_green .btn_detailed_report:hover {
  background: var(--forest_green-color-4);
  -webkit-filter: drop-shadow(0px 0px 0px var(--forest_green-color-1));
  filter: drop-shadow(0px 0px 0px var(--forest_green-color-1));
}

/*Gold*/
.cgold .btn_detailed_report {
  border-color: var(--gold-color-1);
  -webkit-filter: drop-shadow(0px 4px 0px var(--gold-color-1));
  filter: drop-shadow(0px 4px 0px var(--gold-color-1));
}
.cgold .btn_detailed_report:hover {
  background: var(--gold-color-4);
  -webkit-filter: drop-shadow(0px 0px 0px var(--gold-color-1));
  filter: drop-shadow(0px 0px 0px var(--gold-color-1));
}

/*brick*/
.cbrick .btn_detailed_report {
  border-color: var(--brick-color-1);
  -webkit-filter: drop-shadow(0px 4px 0px var(--brick-color-1));
  filter: drop-shadow(0px 4px 0px var(--brick-color-1));
}
.cbrick .btn_detailed_report:hover {
  background: var(--brick-color-4);
  -webkit-filter: drop-shadow(0px 0px 0px var(--brick-color-1));
  filter: drop-shadow(0px 0px 0px var(--brick-color-1));
}

/*Fuchsia*/
.cfuchsia .btn_detailed_report {
  border-color: var(--fuchsia-color-1);
  -webkit-filter: drop-shadow(0px 4px 0px var(--fuchsia-color-1));
  filter: drop-shadow(0px 4px 0px var(--fuchsia-color-1));
}
.cfuchsia .btn_detailed_report:hover {
  background: var(--fuchsia-color-4);
  -webkit-filter: drop-shadow(0px 0px 0px var(--fuchsia-color-1));
  filter: drop-shadow(0px 0px 0px var(--fuchsia-color-1));
}

.p-card .p-card-content {
  padding: 1rem 0 0 !important;
}

.mobile-only {
  display: inherit;
}

/*2 Level*/
.pl2_red {
  background-color: #ff706a;
}
.pl2_green {
  background-color: #98fa65;
}
/*3 Level*/
.pl3_red {
  background-color: #ff4840;
}
.pl3_yellow {
  background-color: #ffcb4d;
}
.pl3_green {
  background-color: #6fe830;
}
/*4 Level*/
.pl4_red {
  background-color: #ed0c00;
}
.pl4_yellow {
  background-color: #f5ae00;
}
.pl4_green {
  background-color: #45c304;
}
.pl4_blue {
  background-color: #1a9eff;
}
/*5 Level*/
.pl5_red {
  background-color: #b70000;
}
.pl5_yellow {
  background-color: #dd8800;
}
.pl5_green {
  background-color: #308e00;
}
.pl5_blue {
  background-color: #185cc3;
}
.pl5_purple {
  background-color: #7327e3;
}

/* default one level pld color */
.pl1 {
  background-color: #1a9eff;
}

/* .score_box {
  color: #000;
  font-size: 3rem;
  font-weight: 700;
  line-height: 1;
  margin: 0 auto 1rem;
} */
/* .score_box_layout:has(.barrel) .score_box {
  background-color: #f3f3f3;
  border-radius: 0.25rem;
  height: 64px;
  margin: 0 auto;
  max-width: 100%;
  padding: 8px 10px;
  text-align: center;
  width: 150px;
} */
/* .score_box_layout .score_box text {
  fill: #000;
}
.score_box_layout:has(.barrel) .score_box text {
  fill: currentColor;
}
.score_box_layout:has(.barrel) .score_box.pl1_1 {
  background-color: #1a9eff15;
  color: #1a9eff;
}
.score_box_layout:has(.barrel) .score_box.pl2_1 {
  background-color: #ff706a15;
  color: #ff706a;
}
.score_box_layout:has(.barrel) .score_box.pl2_2 {
  background-color: #98fa6515;
  color: #98fa65;
}
.score_box_layout:has(.barrel) .score_box.pl3_1 {
  background-color: #ff484015;
  color: #ff4840;
}
.score_box_layout:has(.barrel) .score_box.pl3_2 {
  background-color: #ffcb4d15;
  color: #ffcb4d;
}
.score_box_layout:has(.barrel) .score_box.pl3_3 {
  background-color: #6fe83015;
  color: #6fe830;
}
.score_box_layout:has(.barrel) .score_box.pl4_1 {
  background-color: #ed0c0015;
  color: #ed0c00;
}
.score_box_layout:has(.barrel) .score_box.pl4_2 {
  background-color: #f5ae0015;
  color: #f5ae00;
}
.score_box_layout:has(.barrel) .score_box.pl4_3 {
  background-color: #45c30415;
  color: #45c304;
}
.score_box_layout:has(.barrel) .score_box.pl4_4 {
  background-color: #1a9eff15;
  color: #1a9eff;
}
.score_box_layout:has(.barrel) .score_box.pl5_1 {
  background-color: #b7000015;
  color: #b70000;
}
.score_box_layout:has(.barrel) .score_box.pl5_2 {
  background-color: #dd880015;
  color: #dd8800;
}
.score_box_layout:has(.barrel) .score_box.pl5_3 {
  background-color: #308e0015;
  color: #308e00;
}
.score_box_layout:has(.barrel) .score_box.pl5_4 {
  background-color: #185cc315;
  color: #185cc3;
}
.score_box_layout:has(.barrel) .score_box.pl5_5 {
  background-color: #7327e315;
  color: #7327e3;
} */

/* AR Overwrites */
/* :has(.ATLAS.Screener) .score_box_layout:has(.barrel) .score_box.pl2_1 {
  background-color: #f6933115;
  color: #f69331;
}
:has(.ATLAS.Screener) .score_box_layout:has(.barrel) .score_box.pl2_2 {
  background-color: #1a9eff15;
  color: #1a9eff;
}
:has(.ATLAS.Summative, .ATLAS.Interim) .score_box_layout:has(.barrel) .score_box.pl4_1 {
  background-color: #efaf0015;
  color: #efaf00;
}
:has(.ATLAS.Summative, .ATLAS.Interim) .score_box_layout:has(.barrel) .score_box.pl4_2 {
  background-color: #79d59b15;
  color: #79d59b;
}
:has(.ATLAS.Summative, .ATLAS.Interim) .score_box_layout:has(.barrel) .score_box.pl4_3 {
  background-color: #2eac5d15;
  color: #2eac5d;
}
:has(.ATLAS.Summative, .ATLAS.Interim) .score_box_layout:has(.barrel) .score_box.pl4_4 {
  background-color: #1a9eff15;
  color: #1a9eff;
} */

.level_text,
.percentile_text {
  color: var(--bs-text);
  font-size: 0.875rem;
  line-height: 1.35;
}
.level_text span {
  font-weight: bold;
}
.level_text .FAST.level_1 {
  color: #b70000;
}
.level_text .FAST.level_2 {
  color: #d18100;
}
.level_text .FAST.level_3 {
  color: #308e00;
}
.level_text .FAST.level_4 {
  color: #185cc3;
}
.level_text .FAST.level_5 {
  color: #7327e3;
}
.percentile_text span {
  color: var(--bs-primary);
  font-weight: bold;
}
body.texas .percentile_text span {
  color: var(--bs-black);
  font-weight: 400;
}

/* Tables */
.table_container {
  overflow: auto;
}
.report-table {
  border-collapse: collapse;
  display: table;
  width: 100%;
}
/* 
.toggle-description.k-2_header {
  display: none;
} */

body.florida .b_e_s_t_ .toggle-description .toggle-btn,
body.florida .B_E_S_T_ .toggle-description .toggle-btn,
body.florida .eoc .toggle-description .toggle-btn,
body.florida .EOC .toggle-description .toggle-btn,
body.florida .faa .toggle-description .toggle-btn,
body.florida .FAA .toggle-description .toggle-btn,
body.florida .fast .toggle-description .toggle-btn,
body.florida .FAST .toggle-description .toggle-btn,
body.florida .fcle .toggle-description .toggle-btn,
body.florida .FCLE .toggle-description .toggle-btn,
body.florida .science .toggle-description .toggle-btn,
body.florida .Science .toggle-description .toggle-btn,
body.newhampshire .toggle-description .toggle-btn,
body.westvirginia .toggle-description .toggle-btn {
  display: none;
}

@media screen and (min-width: 425px) {
  #top_bar .page_buttons_inbox button .btn_wrapper,
  #top_bar:has(.page_buttons_inbox) .page_buttons_container button .btn_wrapper,
  #top_bar:not(:has(.page_buttons_inbox)) .page_buttons_container button .btn_wrapper {
    flex-flow: row nowrap;
  }
  #top_bar .page_buttons_inbox button .btn_wrapper .svg-icon,
  #top_bar:has(.page_buttons_inbox) .page_buttons_container button .btn_wrapper .svg-icon,
  #top_bar:not(:has(.page_buttons_inbox)) .page_buttons_container button .btn_wrapper .svg-icon {
    margin: 0 0.375rem 0 0;
  }
  .report_buttons:has(.btn_view_report) .btn_detailed_report {
    align-self: end;
    justify-self: end;
  }
  .report_buttons:has(.btn_view_report) .btn_detailed_report:last-child {
    align-self: start;
    justify-self: start;
  }
  .fullwidth .test_actions:has(.btn_view_report) .btn_detailed_report,
  .tabs .test_actions:has(.btn_view_report) .btn_detailed_report {
    justify-self: end;
  }
  .fullwidth .test_actions:has(.btn_view_report) .btn_detailed_report.btn_download_report,
  .tabs .test_actions:has(.btn_view_report) .btn_detailed_report.btn_download_report {
    justify-self: start;
  }
}

@media screen and (min-width: 768px) {
  .loginpage {
    justify-content: center;
  }
  #top_bar .page_buttons_inbox button .btn_wrapper,
  #top_bar:has(.page_buttons_inbox) .page_buttons_container button .btn_wrapper,
  #top_bar:not(:has(.page_buttons_inbox)) .page_buttons_container button .btn_wrapper {
    flex-flow: column nowrap;
  }
  #top_bar .page_buttons_inbox button .btn_wrapper .svg-icon,
  #top_bar:has(.page_buttons_inbox) .page_buttons_container button .btn_wrapper .svg-icon,
  #top_bar:not(:has(.page_buttons_inbox)) .page_buttons_container button .btn_wrapper .svg-icon {
    margin: 0 0 0.375rem;
  }
  :not(.fullwidth, .download-con) .btn_detailed_report .btn_wrapper {
    grid-row-gap: 0.25rem;
    grid-template-columns: 1fr;
    min-height: 74px;
    width: 120px;
  }
  :not(.fullwidth, .download-con) .report_buttons .btn_detailed_report .btn_wrapper {
    grid-template-columns: 31px 84px;
    grid-template-rows: 1fr;
    min-height: inherit;
    width: 144px;
  }
  .fullwidth .test_actions .btn_detailed_report,
  .fullwidth .test_actions .btn_detailed_report.btn_download_report {
    align-self: center;
    justify-self: center;
  }
  .fullwidth .test_actions:has(.btn_view_report) .btn_detailed_report,
  .tabs .test_actions:has(.btn_view_report) .btn_detailed_report {
    align-self: end;
    justify-self: center;
  }
  .fullwidth .test_actions:has(.btn_view_report) .btn_detailed_report.btn_download_report,
  .tabs .test_actions:has(.btn_view_report) .btn_detailed_report.btn_download_report {
    align-self: start;
    justify-self: center;
  }
  .mobile-only {
    display: none !important;
  }
  .p-dialog.tsds-id-modal {
    width: 50vw !important;
    max-width: 600px;
  }
}

@media screen and (min-width: 1024px) {
  .fullwidth .btn_detailed_report .btn_wrapper,
  .tabs .test_actions .btn_detailed_report .btn_wrapper {
    grid-template-columns: 98px;
    grid-template-rows: 24px 1fr;
    grid-row-gap: 0.25rem;
  }
  .fullwidth .test_actions .btn_detailed_report {
    justify-self: end;
  }
  .fullwidth .test_actions:has(.btn_view_report) .btn_detailed_report,
  .tabs .test_actions:has(.btn_view_report) .btn_detailed_report {
    align-self: center;
    justify-self: end;
  }
  .fullwidth .test_actions:has(.btn_view_report) .btn_detailed_report.btn_download_report,
  .tabs .test_actions:has(.btn_view_report) .btn_detailed_report.btn_download_report {
    align-self: center;
    justify-self: start;
  }
  .tabs .test_actions:has(.btn_view_report) .btn_detailed_report {
    align-self: end;
    justify-self: center;
  }
  .tabs .test_actions:has(.btn_view_report) .btn_detailed_report.btn_download_report {
    align-self: start;
    justify-self: center;
  }
}
@media screen and (max-width: 767px) {
  #top_bar {
    flex-flow: column nowrap;
  }
  #top_bar button {
    padding: 0 0.5rem;
  }
  #student_info {
    flex-flow: row wrap;
  }
  #top_bar .page_buttons_container button {
    padding: 0;
  }
  .footerWrapper {
    padding-bottom: 1em !important;
  }
  #app footer {
    flex-flow: row wrap;
    justify-content: center;
    padding: 0.5em 2em 1em !important;
  }
  .texas #app footer {
    padding: 0.5em 2em 0 !important;
  }
  .mobile_tabs {
    display: flex !important;
  }
  [role="tablist"] {
    display: none !important;
  }
  header {
    top: 0 !important;
    width: 100% !important;
  }
  .fullwidth .subject_icon {
    margin-left: 0 !important;
  }
  .selected_subject .subject_icon {
    height: 50px;
    left: 0;
    margin-right: 12px;
    position: relative;
    width: 50px;
    display: inline-flex;
  }
  .filter_row.selected_subject {
    display: flex;
    align-items: center;
  }
  /* Colors */
  .cblue .tabpanel {
    border-color: var(--blue-color-2) !important;
  }
  .cchartreuse .tabpanel {
    border-color: var(--chartreuse-color-2) !important;
  }
  .cgreen .tabpanel {
    border-color: var(--green-color-2) !important;
  }
  .cmagenta .tabpanel {
    border-color: var(--magenta-color-2) !important;
  }
  .corange .tabpanel {
    border-color: var(--orange-color-2) !important;
  }
  .cpink .tabpanel {
    border-color: var(--pink-color-2) !important;
  }
  .cpurple .tabpanel {
    border-color: var(--purple-color-2) !important;
  }
  .csalmon .tabpanel {
    border-color: var(--salmon-color-2) !important;
  }
  .cseafoam .tabpanel {
    border-color: var(--seafoam-color-2) !important;
  }
  .cteal .tabpanel {
    border-color: var(--teal-color-2) !important;
  }
  .cforest_green .tabpanel {
    border-color: var(--forest_green-color-2) !important;
  }
  .cgold .tabpanel {
    border-color: var(--gold-color-2) !important;
  }
  .cbrick .tabpanel {
    border-color: var(--brick-color-2) !important;
  }
  .cfuchsia .tabpanel {
    border-color: var(--fuchsia-color-2) !important;
  }
  /* Subject Page */
  .tabPanels {
    margin-top: 0 !important;
  }

  .tabpanel {
    border-radius: 0 0 10px 10px !important;
  }
  .tabpanel header {
    border-radius: 0 !important;
    padding: 0.7em 1em !important;
  }
  .tabpanel header::before {
    display: none;
  }
  /* Content */
  .dflex-cl {
    flex-flow: column nowrap;
  }
  .texas .dflex-cl {
    padding-top: 0;
  }
  .report-con-left {
    display: none;
  }
  #tab_overall .report-con-left {
    display: flex;
    margin: 0 0 1em !important;
    padding: 0 !important;
    width: 100% !important;
  }
  #tab_overall .report-con-left div {
    width: 100% !important;
  }
  #tab_overall .download-con {
    text-align: center;
  }
  #tab_overall .download-con .btn_detailed_report {
    font-size: 1em;
    height: 6.944em;
    width: 8.333em;
  }
  #tab_overall .download-con .btn_detailed_report .btn_text {
    margin-top: 0.25em;
  }
  .detail-right-con {
    border-left: none !important;
    border-right: none !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  .performance_icon,
  .percentile_number {
    margin: 0 auto;
  }
  .resourcemodalclass.v-dialog:not(.v-dialog--fullscreen) {
    max-height: inherit;
    max-width: inherit;
  }
}
@media screen and (min-height: 800px) {
  .loginpage {
    height: 100%;
  }
}
/* @media screen and (min-width: 768px) and (max-width: 1023px) {
  .loginpage {
    justify-content: inherit;
  }
} */
@media screen and (min-width: 768px) and (max-width: 1400px) {
  .topContainer {
    width: 50% !important;
  }
}
@media print {
  body {
    font-size: 0.8rem;
  }
  #app {
    margin: 0;
    padding: 0;
  }
  /* body {
    -webkit-print-color-adjust: exact;
  } */
  .CAIHeader {
    -webkit-print-color-adjust: exact;
  }
  /* .filter_container, */
  .barrel_chart {
    -webkit-print-color-adjust: exact;
  }
  .test_card .most_recent h3 {
    border-color: #ccc !important;
  }
  .v-container .v-container .flex.md6,
  .v-container .v-container .flex.md6:nth-child(even) {
    flex: 0 0 100%;
    margin: 0 0 1em;
    max-width: 100%;
  }
  /* #app {
    font-size: 80%;
  } */
  .tabpanel .test_card {
    width: 100%;
  }
  .iconMathBadge,
  .iconAdvancedMath {
    color-adjust: exact;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .pl_icon,
  .percentile_number {
    color-adjust: exact !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .texas .v-container.footerWrapper.main_wrapper {
    /* position: absolute;
    bottom: 0; */
    margin-top: 1em;
  }
}
</style>
